import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateBulkUpload } from '@steelbuy/data-access';
import { DocumentType } from '@steelbuy/ts-shared';
import { isValidFileType, useRequiredFieldValidator } from '@steelbuy/ui-domain';
import {
    FileUploadStatus,
    InputFile,
    toaster,
    useWizardStepsContext,
    WizardBarItemStatus,
} from '@steelbuy/ui-primitive';
import { FormLayout } from '../../../views/layouts/form-layout/FormLayout';
import { WizardActionBar } from '../../wizard-action-bar/WizardActionBar';
import { useBulkListingContext } from '../bulk-listing-context/BulkListingContext';
import { BulkListingStep } from '../BulkListing.enums';
import { getStepRoute, mapDraftToFormValues } from '../bulkListingUtil';

import './ImportListingsStep.scss';

const VALID_FILES = ['.xls', '.xlsx'];

export const ImportListingsStep = () => {
    const { draftId = '' } = useParams();
    const requiredFieldValidator = useRequiredFieldValidator();
    const { isStepVisited, updateStepStatus, stepStatus } = useWizardStepsContext();
    const { t } = useTranslation();
    const [fileStatus, setFileStatus] = useState({});
    const navigate = useNavigate();
    const { getValues, setValue, control } = useFormContext();
    const { replace } = useFieldArray({
        control,
        name: 'listings',
    });
    const { uploadedFileRef, setShowUploadCompleteNotification } = useBulkListingContext();
    const createBulkUploadMutation = useCreateBulkUpload();

    const hasValidFile = Object.values(fileStatus).some((status) => status === FileUploadStatus.Success);

    const file = getValues('file');

    useEffect(() => {
        if (stepStatus[BulkListingStep.CHOOSE_MATERIAL] === WizardBarItemStatus.EMPTY) {
            updateStepStatus(BulkListingStep.IMPORT_LISTINGS, WizardBarItemStatus.EMPTY);
            navigate(getStepRoute(1, draftId), { replace: true });
            return;
        }
        if (file) {
            setFileStatus({ [file.name]: FileUploadStatus.Success });
        }
    }, []);

    const updateFileStatus = (uploadedFile: File, status: FileUploadStatus) => {
        if (status === FileUploadStatus.Error) {
            setValue('file', undefined);
        }
        if (status === FileUploadStatus.Success) {
            setValue('file', { name: uploadedFile.name, size: uploadedFile.size });
        }
        setFileStatus({ [uploadedFile.name]: status });
    };

    const getInvalidMessage = () => (inputFile: File) => {
        if (!isValidFileType(DocumentType.PACKAGE, inputFile.name)) {
            return t('uiDomain:validation.unsupporttedFileType');
        }
        return undefined;
    };

    return (
        <FormLayout>
            <p className="bulk-listing-import-listing__title">
                {t('application.createBulkListing.importListing.header')}
            </p>
            <InputFile
                label={t('application.createBulkListing.importListing.label')}
                fileType={VALID_FILES}
                onChange={async (files) => {
                    if (files?.[0]) {
                        // eslint-disable-next-line prefer-destructuring
                        uploadedFileRef.current = files[0];
                        const nameAndType = files[0].name.split('.');
                        if (nameAndType.length < 2) {
                            updateFileStatus(files[0], FileUploadStatus.Error);
                            replace([]);
                            return;
                        }

                        const extension = nameAndType.pop()?.toLowerCase();
                        if (!VALID_FILES.includes(`.${extension}`)) {
                            updateFileStatus(files[0], FileUploadStatus.Error);
                            replace([]);
                            return;
                        }
                        setValue('spreadsheet', files[0]);
                        updateFileStatus(files[0], FileUploadStatus.Success);
                    }
                }}
                fileTypeHelperText={t('application.createBulkListing.importListing.fileInputHelperText')}
                validators={[requiredFieldValidator]}
                forceValidation={isStepVisited(BulkListingStep.IMPORT_LISTINGS)}
                fileStatus={fileStatus}
                onRemoveFile={() => {
                    setFileStatus({});
                    setValue('file', undefined);
                    setValue('spreadsheet', undefined);
                }}
                initialSelectedFiles={file ? [file] : undefined}
                getInvalidMessage={getInvalidMessage()}
                data-testid="bulk-upload-file-input"
                isMandatory
            />
            <WizardActionBar
                onNext={() => {
                    if (getValues('spreadsheet')) {
                        if (!uploadedFileRef.current) {
                            updateStepStatus(BulkListingStep.IMPORT_LISTINGS, WizardBarItemStatus.SUCCESS);
                            navigate(getStepRoute(3, draftId));
                            setShowUploadCompleteNotification(false);
                            return;
                        }
                        const { materialType, shape, product, spreadsheet, prime } = getValues();
                        createBulkUploadMutation.mutate(
                            {
                                file: spreadsheet,
                                materialType,
                                shape,
                                product,
                                prime,
                            },
                            {
                                onSuccess: (data) => {
                                    replace(mapDraftToFormValues(data));
                                    updateStepStatus(BulkListingStep.IMPORT_LISTINGS, WizardBarItemStatus.SUCCESS);
                                    navigate(getStepRoute(3, data.bulkId));
                                    setShowUploadCompleteNotification(true);
                                },
                                onError: () => {
                                    toaster.warn(t('application.createBulkListing.importListing.uploadError'));
                                },
                            }
                        );
                    }
                }}
                isSubmitDisabled={!hasValidFile}
                nextLabel={t('application.createRFQ.wizardNextButtonLabel')}
                isLoading={false}
                isSubmitting={createBulkUploadMutation.isLoading}
                onBack={() => {
                    updateStepStatus(
                        BulkListingStep.IMPORT_LISTINGS,
                        hasValidFile ? WizardBarItemStatus.SUCCESS : WizardBarItemStatus.INCOMPLETE
                    );
                    navigate(getStepRoute(1, draftId), { replace: true });
                }}
            />
        </FormLayout>
    );
};
